import type { SkuBenchmark } from '../../data/analytics';
import type { ApiDetail } from '../../features/catalog/model';
import {
  aggregateBenchmarks,
  hasBenchmarkPopulation,
  latencyRange,
  reliabilityBreakdown,
} from '../../lib/benchmarks';
import { ReliabilityMeter } from '../../components/charts/ReliabilityMeter';
import { LatencyRangeChart } from '../../components/charts/LatencyRangeChart';
import { Band } from '../../components/editorial/Band';
import { Eyebrow } from '../../components/Eyebrow';
import { m } from '../../paraglide/messages';

/**
 * Real, AnyAPI-measured performance for a platform's endpoints over the last 30
 * days, through the gateway (Concept A): a reliability meter (success split
 * across first-lane / failover-recovered / failed) beside a latency range. This
 * is first-hand data no competitor has; it renders only when the measured
 * sample is large enough (aggregateBenchmarks returns null below the floor).
 *
 * When the numbers on screen legitimately omit a caller-requested wait, the
 * footnote is the gateway's own `latencyNote` string, rendered verbatim. The
 * wording is deliberately NOT duplicated here: one sentence, one owner, no way
 * for the disclosure to drift away from the definition it describes.
 */
export const BenchmarkPanel = ({
  api,
  benchmarks,
}: {
  api: ApiDetail;
  benchmarks: Record<string, SkuBenchmark>;
}) => {
  const skus = api.endpoints.map((e) => e.sku);
  const summary = aggregateBenchmarks(skus, benchmarks);
  if (!summary) return null;
  const showUptime = hasBenchmarkPopulation(summary.uptimeSample);
  const showLatency = hasBenchmarkPopulation(summary.latencySample);

  return (
    <Band wash="wash">
      <div className="band grid gap-x-16 gap-y-10 lg:grid-cols-[5fr_7fr]">
        <div>
          <Eyebrow>{m.platform_benchmark_eyebrow()}</Eyebrow>
          <h2 className="display-3 mt-4 text-text">
            {m.platform_benchmark_heading({ name: api.name })}
          </h2>
          <p className="lede mt-5 max-w-[40ch]">
            {m.platform_benchmark_lede()}
          </p>
          {showLatency && summary.excludesCallerDelay && summary.latencyNote ? (
            <p className="mt-5 text-12 leading-[1.6] text-text-3">{summary.latencyNote}</p>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2">
          {showUptime && (
            <ReliabilityMeter
              successPct={summary.uptimePct}
              sample={summary.uptimeSample}
              breakdown={reliabilityBreakdown(summary)}
            />
          )}
          {showLatency && (
            <LatencyRangeChart
              range={latencyRange(summary)}
              sample={summary.latencySample}
              estimated={summary.latencyIsEstimate}
            />
          )}
        </div>
      </div>
    </Band>
  );
};
